import React from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, TextField, MenuItem, Typography, CircularProgress,
  Box, Alert, FormControl, InputLabel, Select
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const GEMINI_MODEL_OPTIONS = [
  { value: 'gemini-1.5-pro-001', label: 'Gemini 1.5 Pro' },
  { value: 'gemini-1.5-flash-001', label: 'Gemini 1.5 Flash (Faster)' },
  { value: 'gemini-1.5-ultra-001', label: 'Gemini 1.5 Ultra (Most powerful)' },
  { value: 'gemini-2.0-flash-001', label: 'Gemini 2.0 Flash' },
  { value: 'gemini-2.5-flash-preview-04-17', label: 'Gemini 2.5 Flash Preview (Fastest)' },
  { value: 'gemini-2.5-pro-exp-03-25', label: 'Gemini 2.5 Pro (Experimental)' },
  { value: 'gemini-2.5-pro-preview-05-06', label: 'Gemini 2.5 Pro Preview' },
  { value: 'gemini-3.1-flash', label: 'Gemini 3.1 Flash' },
  { value: 'gemini-3.1-pro', label: 'Gemini 3.1 Pro' },
];

const OTHER_MODEL_OPTIONS = [
  { value: 'gpt-4', label: 'OpenAI GPT-4' },
  { value: 'gpt-3.5-turbo', label: 'OpenAI GPT-3.5 Turbo' },
  { value: 'claude-3', label: 'Anthropic Claude 3' },
];

// Define a constant for the "all levels" value
const ALL_LEVELS_VALUE = 'all';

// Update schema to accept string or number for level
const ContentGenerationSchema = Yup.object().shape({
  level: Yup.mixed().required('Level is required'),
  additionalPrompt: Yup.string().max(500, 'Prompt is too long (max 500 characters)'),
  model: Yup.string().required('Model selection is required')
});

export default function ContentGenerationDialog({ 
  open, 
  onClose, 
  onSubmit, 
  courseName, 
  themeName,
  isGenerating,
  generatingLevel  // New optional prop to show which level is currently generating
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate Content Blocks with AI</DialogTitle>
      <Formik
        initialValues={{
          level: 1,
          additionalPrompt: '',
          model: 'gemini-3.1-pro' // Default to Gemini 3.1 Pro
        }}
        validationSchema={ContentGenerationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched, values }) => (
          <Form>
            <DialogContent>
              <Alert severity="info" sx={{ mb: 3 }}>
                This will generate content blocks for the theme "{themeName}" in course "{courseName}".
                You can specify the difficulty level and provide additional instructions.
              </Alert>
              
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Difficulty Level:
                </Typography>
                <Field name="level">
                  {({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Select Level</InputLabel>
                      <Select
                        {...field}
                        label="Select Level"
                        error={touched.level && Boolean(errors.level)}
                      >
                        <MenuItem value={ALL_LEVELS_VALUE}>All Levels</MenuItem>
                        <MenuItem value={1}>Level 1 - Basic</MenuItem>
                        <MenuItem value={2}>Level 2 - Intermediate</MenuItem>
                        <MenuItem value={3}>Level 3 - Deep</MenuItem>
                      </Select>
                      {touched.level && errors.level && (
                        <Typography color="error" variant="caption">{errors.level}</Typography>
                      )}
                    </FormControl>
                  )}
                </Field>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {values.level === ALL_LEVELS_VALUE 
                    ? "Generate content blocks for all three difficulty levels at once (takes longer)."
                    : "This will generate multiple content blocks appropriate for the selected level."}
                </Typography>
              </Box>
              
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  AI Model:
                </Typography>
                <Field name="model">
                  {({ field }) => (
                    <FormControl fullWidth error={touched.model && Boolean(errors.model)}>
                      <InputLabel>Select AI Model</InputLabel>
                      <Select
                        {...field}
                        label="Select AI Model"
                      >
                        <MenuItem disabled sx={{ opacity: 0.7 }}>
                          <Typography fontWeight="bold">
                            Gemini Models
                          </Typography>
                        </MenuItem>
                        {GEMINI_MODEL_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                        
                        <MenuItem disabled sx={{ opacity: 0.7, mt: 1 }}>
                          <Typography fontWeight="bold">
                            Other Models
                          </Typography>
                        </MenuItem>
                        {OTHER_MODEL_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.model && errors.model && (
                        <Typography color="error" variant="caption">{errors.model}</Typography>
                      )}
                    </FormControl>
                  )}
                </Field>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Different models have different strengths. Gemini 3.1 Pro offers the best balance of quality and speed.
                </Typography>
              </Box>
              
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Additional Instructions (Optional):
                </Typography>
                <Field name="additionalPrompt">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Provide any specific instructions, content focus, or teaching style suggestions for the AI."
                      error={touched.additionalPrompt && Boolean(errors.additionalPrompt)}
                      helperText={touched.additionalPrompt && errors.additionalPrompt}
                    />
                  )}
                </Field>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  For example: "Focus on practical examples" or "Include quiz questions at the end of each block"
                </Typography>
              </Box>

              {isGenerating && (
                <Box display="flex" justifyContent="center" my={3} flexDirection="column" alignItems="center">
                  <CircularProgress size={40} />
                  <Typography mt={2}>
                    {values.level === ALL_LEVELS_VALUE && generatingLevel 
                      ? `Generating Level ${generatingLevel} content...` 
                      : 'Generating content blocks...'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {values.level === ALL_LEVELS_VALUE 
                      ? "Generating content for all difficulty levels. This may take 1-2 minutes."
                      : "This may take 30-60 seconds"}
                  </Typography>
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} disabled={isGenerating}>Cancel</Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={isGenerating || isSubmitting}
                startIcon={isGenerating && <CircularProgress size={20} color="inherit" />}
              >
                {isGenerating ? 'Generating...' : 'Generate Content'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
